import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { getDateUsingFormat } from 'wisestep-base';

import { PotentialRevenueWidget } from '../../../../composableAnalytics/DerivedCharts/helper';
import { renderCellFor } from '../../helper';
import { IPotentialRevenueDTO } from '../../jobs/types/postgreJobsDTO';

export const getPotentialRevenueColumns = (widgetName: PotentialRevenueWidget): GridColDef[] => {
  const renderRevenueCell = (param: GridRenderCellParams<any, any, any>, timeSpan = true) =>
    ((param.value as string) || '0 USD')
      .split(',')
      .filter((val) => val.trim())
      .map((val) => {
        const [value, currency] = val.trim().split(' ');
        const localeValue = Number(value == 'null' ? null : value).toLocaleString();
        const salaryTimeSpan = timeSpan ? salaryTimeSpanMap[param.row.salaryTimeSpan] ?? '' : '';
        return currencyNameSymbolMap[currency]
          ? `${currencyNameSymbolMap[currency]}${localeValue} ${salaryTimeSpan}`
          : `${localeValue}${currency ? ` ${currency} ${salaryTimeSpan}` : ''}`;
      })
      .join(', ');
  const renderTextCell = (param) =>
    param.value == null || param.value == '__MISSING__' ? 'Not Available' : param.value;

  const column = {
    CLIENT: {
      field: 'clientName',
      headerName: 'Client Name',
      minWidth: 120,
      flex: 1.5,
      renderCell: (param) => renderCellFor('CLIENT', param),
    },
    CLIENT_CONTACT: {
      field: 'clientRecruiterName',
      headerName: 'Client SPOC',
      minWidth: 160,
      flex: 1.5,
      renderCell: (param) => renderCellFor('CLIENT_RECRUITER', param),
    },
    GROSS_REVENUE: (hide = false) => ({
      field: 'grossRevenue',
      headerName: 'Gross Revenue',
      minWidth: 160,
      flex: 1,
      renderCell: (param) => renderRevenueCell(param, false),
      hide,
    }),
    NET_REVENUE: {
      field: 'netRevenue',
      headerName: 'Net Revenue',
      minWidth: 160,
      flex: 1,
      renderCell: (param) => renderRevenueCell(param, false),
    },
    NET_MARGIN: {
      field: 'netMargin',
      headerName: 'Net Margin',
      minWidth: 160,
      flex: 1,
      renderCell: renderRevenueCell,
    },
    ACCOUNT_MANAGER: {
      field: 'accountManagerName',
      headerName: 'Account Manager',
      minWidth: 160,
      flex: 1.5,
      renderCell: (param) => renderCellFor('ACCOUNT_MANAGER', param),
    },
    CANDIDATE: {
      field: 'candidateName',
      headerName: 'Candidate Name',
      minWidth: 160,
      flex: 1.5,
      renderCell: (param) => renderCellFor('CANDIDATE', param),
    },
    STAGE: (hide = false) => ({
      field: 'applicationStage',
      headerName: 'Stage',
      minWidth: 110,
      flex: 1,
      renderCell: renderTextCell,
      hide,
    }),
    STATE: (hide = false) => ({
      field: 'applicationState',
      headerName: 'State',
      minWidth: 130,
      flex: 1,
      renderCell: renderTextCell,
      hide,
    }),
    UPDATE_DATE: (hide = false) => ({
      field: 'modifiedOn',
      headerName: 'Updated Date',
      minWidth: 130,
      flex: 1,
      renderCell: (params) => (params.value ? getDateUsingFormat(params.value, 'YYYY-MM-DD') : ''),
      hide,
    }),
    JOINING_DATE: (hide = false) => ({
      field: 'joiningDate',
      headerName: 'Joining Date',
      minWidth: 130,
      flex: 1,
      renderCell: (params) => (params.value ? getDateUsingFormat(params.value, 'YYYY-MM-DD') : ''),
      hide,
    }),
    JOB_TITLE: (hide = false) => ({
      field: 'jobTitle',
      headerName: 'Job Title (Job Code)',
      minWidth: 300,
      flex: 1,
      renderCell: (param) =>
        renderCellFor('JOB', param, `${param.row.jobTitle} (${param.row.jobCode})`),
      hide,
    }),
  };

  switch (widgetName) {
    case PotentialRevenueWidget.TABLE_CLIENT_PERMANENT:
    case PotentialRevenueWidget.TABLE_CLIENT_CONTRACT:
      return [column.CLIENT, column.GROSS_REVENUE(), column.NET_REVENUE, column.NET_MARGIN];
    case PotentialRevenueWidget.TABLE_CLIENT_CONTACT_PERMANENT:
    case PotentialRevenueWidget.TABLE_CLIENT_CONTACT_CONTRACT:
      return [
        column.CLIENT_CONTACT,
        column.CLIENT,
        column.GROSS_REVENUE(),
        column.NET_REVENUE,
        column.NET_MARGIN,
      ];
    case PotentialRevenueWidget.TABLE_AM_CONTRACT:
    case PotentialRevenueWidget.TABLE_AM_PERMANENT:
      return [
        column.ACCOUNT_MANAGER,
        column.CLIENT,
        column.GROSS_REVENUE(),
        column.NET_REVENUE,
        column.NET_MARGIN,
      ];
    case PotentialRevenueWidget.TABLE_CANDIDATE:
    case PotentialRevenueWidget.TABLE_CANDIDATE_BDEF:
      return [
        column.CANDIDATE,
        column.JOB_TITLE(widgetName == PotentialRevenueWidget.TABLE_CANDIDATE),
        column.CLIENT,
        column.ACCOUNT_MANAGER,
        column.STAGE(widgetName == PotentialRevenueWidget.TABLE_CANDIDATE),
        column.STATE(widgetName == PotentialRevenueWidget.TABLE_CANDIDATE),
        column.UPDATE_DATE(widgetName == PotentialRevenueWidget.TABLE_CANDIDATE),
        column.JOINING_DATE(widgetName == PotentialRevenueWidget.TABLE_CANDIDATE),
        column.GROSS_REVENUE(widgetName == PotentialRevenueWidget.TABLE_CANDIDATE_BDEF),
        column.NET_REVENUE,
        column.NET_MARGIN,
      ];
    default:
      throw new Error("Invalid 'PotentialRevenueWidget' type");
  }
};

export const defaultPotentialRevenueWidgetData: IPotentialRevenueDTO = {
  totalRevenue: 0,
  currency: 'XXX',
  resultSet: [],
};

const salaryTimeSpanMap: Record<string, string> = {
  PerHour: '/ hour',
  PerDay: '/ day',
  PerMonth: '/ month',
};

export const currencyNameSymbolMap: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  INR: '₹',
  AUD: 'A$',
  CAD: 'C$',
  SGD: 'S$',
  HKD: 'HK$',
  JPY: '¥',
  CNY: '¥',
  KRW: '₩',
  TWD: 'NT$',
  MYR: 'RM',
  THB: '฿',
  IDR: 'Rp',
  PHP: '₱',
  VND: '₫',
  LKR: 'Rs',
  PKR: '₨',
  BDT: '৳',
  NPR: 'Rs',
  EGP: 'E£',
  ZAR: 'R',
  NGN: '₦',
  KES: 'KSh',
  GHS: 'GH₵',
  TZS: 'TSh',
  UGX: 'USh',
};
